/**
 * Pull to Refresh Hook
 * 
 * Provides swipe down to refresh functionality for mobile/native apps
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { isNativeApp } from '@/utils/platformUtils';

interface PullToRefreshOptions {
  onRefresh: () => Promise<void> | void;
  threshold?: number;
  resistance?: number;
  enabled?: boolean;
}

interface PullToRefreshState {
  isPulling: boolean;
  pullDistance: number;
  isRefreshing: boolean;
  canRefresh: boolean;
}

export function usePullToRefresh({
  onRefresh,
  threshold = 80,
  resistance = 2.5,
  enabled = true
}: PullToRefreshOptions) {
  const isMobile = useIsMobile();
  const isNative = isNativeApp();
  const containerRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<PullToRefreshState>({
    isPulling: false,
    pullDistance: 0,
    isRefreshing: false,
    canRefresh: false
  });

  const touchStartY = useRef<number>(0);
  const touchCurrentY = useRef<number>(0);
  const isScrolledToTop = useRef<boolean>(true);

  const checkScrollPosition = useCallback(() => {
    if (!containerRef.current) return;
    
    const scrollTop = containerRef.current.scrollTop || window.scrollY;
    isScrolledToTop.current = scrollTop <= 0;
  }, []);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (!enabled || !isMobile || state.isRefreshing) return;
    
    checkScrollPosition();
    if (!isScrolledToTop.current) return;

    touchStartY.current = e.touches[0].clientY;
    touchCurrentY.current = e.touches[0].clientY;
  }, [enabled, isMobile, state.isRefreshing, checkScrollPosition]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!enabled || !isMobile || state.isRefreshing) return;
    if (!isScrolledToTop.current) return;

    touchCurrentY.current = e.touches[0].clientY;
    const pullDistance = Math.max(0, (touchCurrentY.current - touchStartY.current) / resistance);

    if (pullDistance > 0) {
      e.preventDefault(); // Prevent default scroll behavior
      
      setState(prev => ({
        ...prev,
        isPulling: true,
        pullDistance,
        canRefresh: pullDistance >= threshold
      }));
    }
  }, [enabled, isMobile, state.isRefreshing, resistance, threshold]);

  const handleTouchEnd = useCallback(async () => {
    if (!enabled || !isMobile || state.isRefreshing) return;

    if (state.canRefresh && state.pullDistance >= threshold) {
      setState(prev => ({
        ...prev,
        isRefreshing: true,
        isPulling: false,
        pullDistance: 0
      }));

      try {
        await onRefresh();
      } catch (error) {
        console.error('Pull to refresh failed:', error);
      } finally {
        setState(prev => ({
          ...prev,
          isRefreshing: false,
          canRefresh: false
        }));
      }
    } else {
      setState(prev => ({
        ...prev,
        isPulling: false,
        pullDistance: 0,
        canRefresh: false
      }));
    }
  }, [enabled, isMobile, state.isRefreshing, state.canRefresh, state.pullDistance, threshold, onRefresh]);

  useEffect(() => {
    if (!enabled || !isMobile) return;

    const container = containerRef.current;
    if (!container) return;

    // Add event listeners
    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });
    container.addEventListener('scroll', checkScrollPosition, { passive: true });

    // Initial scroll position check
    checkScrollPosition();

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('scroll', checkScrollPosition);
    };
  }, [enabled, isMobile, handleTouchStart, handleTouchMove, handleTouchEnd, checkScrollPosition]);

  // For native apps, also listen to window events as fallback
  useEffect(() => {
    if (!enabled || !isNative) return;

    window.addEventListener('touchstart', handleTouchStart, { passive: false });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [enabled, isNative, handleTouchStart, handleTouchMove, handleTouchEnd]);

  return {
    containerRef,
    ...state,
    // Helper to get pull indicator transform
    getPullIndicatorStyle: () => ({
      transform: `translateY(${Math.min(state.pullDistance, threshold)}px)`,
      opacity: Math.min(state.pullDistance / threshold, 1),
      transition: state.isPulling ? 'none' : 'transform 0.3s ease-out, opacity 0.3s ease-out'
    })
  };
}
